import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import axios from 'axios';
import styles from './CreateTournament.module.scss';
import { useGetAvailableGames } from '../../hooks/useGetAvailableGames';
import { ICreateTournamentRequest } from '../../types/ITournament';
import { ENDPOINTS, QueryKeys } from '../../constants';
import { GreenButton } from '../Shared/GreenButton/GreenButton';
import { TransparentButton } from '../Shared/TransparentButton/TransparentButton';
import { Input } from '../Shared/Input/Input';
import { Loader } from '../Shared/Loader/Loader';
import { Checkbox } from '../Shared/Checkbox/Checkbox';

const emptyForm = {
    name: '',
    game: '',
    gameVersion: '',
    mode: '',
    platform: '',
    startDate: '',
    rounds: 4,
    bet: 0,
};

export const CreateTournament = () => {
    const navigate = useNavigate();
    const { data: availableGames, isLoading: gamesLoading } = useGetAvailableGames();
    const queryClient = useQueryClient();

    const [form, setForm] = useState(emptyForm);
    const [prizes, setPrizes] = useState<string[]>(['']);
    const [forceIncompleteStart, setForceIncompleteStart] = useState(false);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const uniqueGames = useMemo(() => {
        if (!availableGames) return [];
        const seen = new Set<string>();
        return availableGames.filter(g => {
            if (seen.has(g.game)) return false;
            seen.add(g.game);
            return true;
        });
    }, [availableGames]);

    const versionsForGame = useMemo(() => {
        if (!availableGames || !form.game) return [];
        return availableGames.filter(g => g.game === form.game);
    }, [availableGames, form.game]);

    const selectedEntry = useMemo(
        () => versionsForGame.find(g => g.version === form.gameVersion) ?? null,
        [versionsForGame, form.gameVersion]
    );

    const handleGameChange = (game: string) => {
        setForm(f => ({ ...f, game, gameVersion: '', mode: '', platform: '' }));
    };

    const handleVersionChange = (version: string) => {
        setForm(f => ({ ...f, gameVersion: version, mode: '', platform: '' }));
    };

    const handlePrizeChange = (index: number, value: string) => {
        setPrizes(prev => prev.map((p, i) => (i === index ? value : p)));
    };

    const addPrize = () => setPrizes(prev => [...prev, '']);
    const removePrize = (index: number) => setPrizes(prev => prev.filter((_, i) => i !== index));

    const isValid =
        form.name.trim() &&
        form.game &&
        form.gameVersion &&
        form.platform &&
        form.startDate &&
        form.rounds > 0 &&
        form.bet >= 0 &&
        prizes.length > 0 &&
        prizes.every(p => p !== '' && !isNaN(Number(p)));

    const handleSubmit = async () => {
        if (!isValid) return;
        setSubmitting(true);
        setError(null);
        try {
            const payload: ICreateTournamentRequest = {
                name: form.name.trim(),
                game: form.game,
                gameVersion: form.gameVersion,
                ...(form.mode ? { mode: form.mode } : {}),
                platform: form.platform,
                startDate: new Date(form.startDate).toISOString(),
                rounds: Number(form.rounds),
                bet: Number(form.bet),
                prizes: prizes.map(p => parseInt(p, 10)),
                forceIncompleteStart,
            };
            await axios.post(`${axios.defaults.baseURL}${ENDPOINTS.adminTournaments}`, payload);
            queryClient.invalidateQueries(QueryKeys.tournamentsList);
            navigate('/tournaments');
        } catch (e: any) {
            setError(e?.response?.data?.detail ?? e?.message ?? 'Failed to create tournament');
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className={styles.container}>
            <div className={styles.header}>
                <div className={styles.backButton} onClick={() => navigate('/tournaments')}>← Back</div>
                <div className={styles.title}>Create Tournament</div>
            </div>

            {gamesLoading ? (
                <Loader />
            ) : (
                <div className={styles.card}>
                    <div className={styles.grid}>
                        <div className={styles.field}>
                            <label className={styles.label}>Name</label>
                            <Input
                                placeholder="Tournament name"
                                value={form.name}
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                                    setForm(f => ({ ...f, name: e.target.value }))
                                }
                            />
                        </div>

                        <div className={styles.field}>
                            <label className={styles.label}>Game</label>
                            <select
                                className={styles.select}
                                value={form.game}
                                onChange={e => handleGameChange(e.target.value)}
                            >
                                <option value="">Select game</option>
                                {uniqueGames.map(g => (
                                    <option key={g.game} value={g.game}>
                                        {g.gameDisplayName || g.game}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className={styles.field}>
                            <label className={styles.label}>Game Version</label>
                            <select
                                className={styles.select}
                                value={form.gameVersion}
                                disabled={!form.game}
                                onChange={e => handleVersionChange(e.target.value)}
                            >
                                <option value="">Select version</option>
                                {versionsForGame.map(g => (
                                    <option key={g.version} value={g.version}>
                                        {g.displayName || g.version}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className={styles.field}>
                            <label className={styles.label}>Mode</label>
                            <select
                                className={styles.select}
                                value={form.mode}
                                disabled={!selectedEntry}
                                onChange={e => setForm(f => ({ ...f, mode: e.target.value }))}
                            >
                                <option value="">Select mode</option>
                                {selectedEntry?.modes?.values?.map(m => (
                                    <option key={m.mode} value={m.mode}>
                                        {m.title || m.mode}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className={styles.field}>
                            <label className={styles.label}>Platform</label>
                            <select
                                className={styles.select}
                                value={form.platform}
                                disabled={!selectedEntry}
                                onChange={e => setForm(f => ({ ...f, platform: e.target.value }))}
                            >
                                <option value="">Select platform</option>
                                {selectedEntry?.platforms?.map(p => (
                                    <option key={p} value={p}>
                                        {p}
                                    </option>
                                ))}
                            </select>
                        </div>

                        <div className={styles.field}>
                            <label className={styles.label}>Start Date</label>
                            <input
                                type="datetime-local"
                                className={styles.select}
                                value={form.startDate}
                                onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))}
                            />
                        </div>

                        <div className={styles.field}>
                            <label className={styles.label}>Rounds</label>
                            <input
                                type="number"
                                min={1}
                                className={styles.select}
                                value={form.rounds}
                                onChange={e =>
                                    setForm(f => ({ ...f, rounds: parseInt(e.target.value, 10) || 0 }))
                                }
                            />
                        </div>

                        <div className={styles.field}>
                            <label className={styles.label}>Entry Fee</label>
                            <input
                                type="number"
                                min={0}
                                step="0.01"
                                className={styles.select}
                                value={form.bet}
                                onChange={e =>
                                    setForm(f => ({ ...f, bet: parseFloat(e.target.value) || 0 }))
                                }
                            />
                        </div>

                        <div className={styles.field}>
                            <label className={styles.label}>Force Start</label>
                            <div className={styles.checkboxWrapper}>
                                <Checkbox
                                    checked={forceIncompleteStart}
                                    onChange={() => setForceIncompleteStart(!forceIncompleteStart)}
                                />
                                <span className={styles.checkboxLabel}>Start tournament even if not full</span>
                            </div>
                        </div>
                    </div>

                    <div className={styles.prizesSection}>
                        <div className={styles.prizeHeader}>
                            <div className={styles.sectionTitle}>Prizes</div>
                            {prizes.length < 3 && (
                                <div className={styles.addPrize} onClick={addPrize}>+ Add Prize</div>
                            )}
                        </div>
                        {prizes.map((prize, index) => (
                            <div key={index} className={styles.prizeRow}>
                                <span className={styles.prizePlace}>#{index + 1}</span>
                                <input
                                    type="number"
                                    min={0}
                                    className={styles.select}
                                    placeholder={`Prize for place ${index + 1}`}
                                    value={prize}
                                    onChange={e => handlePrizeChange(index, e.target.value)}
                                />
                                {prizes.length > 1 && (
                                    <div className={styles.removeBtn} onClick={() => removePrize(index)}>
                                        ✕
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>

                    {error && <div className={styles.error}>{error}</div>}

                    <div className={styles.actions}>
                        <TransparentButton outline onClick={() => navigate('/tournaments')} noMargin>
                            Cancel
                        </TransparentButton>
                        <GreenButton onClick={handleSubmit} disabled={!isValid || submitting} noMargin>
                            {submitting ? 'Creating...' : 'Create Tournament'}
                        </GreenButton>
                    </div>
                </div>
            )}
        </div>
    );
};
